import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Form, FormGroup, Label, Input, Button, Row, Col, InputGroup, InputGroupText } from "reactstrap";
import { toast } from "react-toastify";
import { FaFileInvoice, FaArrowLeft, FaSave } from "react-icons/fa";
import axios from "axios";
import { useSelector } from "react-redux";

const CreateInvoice = () => {
  const navigate = useNavigate();
  const { AuthReducer: { auth } } = useSelector((state) => state);
  
  const [formData, setFormData] = useState({
    invoice_number: "",
    issue_date: new Date().toISOString().split('T')[0],
    due_date: "",
    billed_to_id: "",
    billed_to_name: "",
    billed_to_address: "",
    invoice_type: "booking",
    subtotal_amount: "",
    tax_amount: "",
    discount_amount: "",
    total_amount: "",
    status: "pending",
    payment_method: "",
    transaction_id: "",
    notes: ""
  });

  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-calculate total amount
    if (name === 'subtotal_amount' || name === 'tax_amount' || name === 'discount_amount') {
      const subtotal = parseFloat(name === 'subtotal_amount' ? value : formData.subtotal_amount) || 0;
      const tax = parseFloat(name === 'tax_amount' ? value : formData.tax_amount) || 0;
      const discount = parseFloat(name === 'discount_amount' ? value : formData.discount_amount) || 0;
      const total = subtotal + tax - discount;
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        total_amount: total.toFixed(2)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post("/api/admin/v1/invoices", formData, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      
      toast.success("Invoice created successfully");
      navigate("/invoices");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create invoice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <div className="mb-4 d-flex align-items-center">
            <Button 
              color="link" 
              className="text-white p-0 me-3"
              onClick={() => navigate("/invoices")}
            >
              <FaArrowLeft size={20} />
            </Button>
            <h5 className="mb-0 text-white fs-4">
              <FaFileInvoice className="me-2" />
              Create Invoice
            </h5>
          </div>
        </div>
      </div>

      <Card>
        <CardBody>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Invoice Number *</Label>
                  <Input
                    type="text"
                    name="invoice_number"
                    value={formData.invoice_number}
                    onChange={handleInputChange}
                    placeholder="INV-2024-001"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Issue Date *</Label>
                  <Input
                    type="date"
                    name="issue_date"
                    value={formData.issue_date}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Due Date *</Label>
                  <Input
                    type="date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Billed To ID</Label>
                  <Input
                    type="text"
                    name="billed_to_id"
                    value={formData.billed_to_id}
                    onChange={handleInputChange}
                    placeholder="Customer/Partner ID"
                  />
                </FormGroup>
              </Col>
              <Col md={8}>
                <FormGroup>
                  <Label>Billed To Name *</Label>
                  <Input
                    type="text"
                    name="billed_to_name"
                    value={formData.billed_to_name}
                    onChange={handleInputChange}
                    placeholder="Customer/Partner Name"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label>Billing Address *</Label>
                  <Input
                    type="textarea"
                    name="billed_to_address"
                    value={formData.billed_to_address}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Complete billing address"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Invoice Type *</Label>
                  <Input
                    type="select"
                    name="invoice_type"
                    value={formData.invoice_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="booking">Booking</option>
                    <option value="service">Service</option>
                    <option value="package">Package</option>
                    <option value="commission">Commission</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="cancelled">Cancelled</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Payment Method</Label>
                  <Input
                    type="select"
                    name="payment_method"
                    value={formData.payment_method}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Method</option>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="upi">UPI</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={3}>
                <FormGroup>
                  <Label>Subtotal Amount *</Label>
                  <InputGroup>
                    <InputGroupText>₹</InputGroupText>
                    <Input
                      type="number"
                      name="subtotal_amount"
                      value={formData.subtotal_amount}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      step="0.01"
                      required
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Tax Amount</Label>
                  <InputGroup>
                    <InputGroupText>₹</InputGroupText>
                    <Input
                      type="number"
                      name="tax_amount"
                      value={formData.tax_amount}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Discount Amount</Label>
                  <InputGroup>
                    <InputGroupText>₹</InputGroupText>
                    <Input
                      type="number"
                      name="discount_amount"
                      value={formData.discount_amount}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Total Amount</Label>
                  <InputGroup>
                    <InputGroupText>₹</InputGroupText>
                    <Input
                      type="number"
                      name="total_amount"
                      value={formData.total_amount}
                      readOnly
                      className="bg-light"
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Transaction ID</Label>
                  <Input
                    type="text"
                    name="transaction_id"
                    value={formData.transaction_id}
                    onChange={handleInputChange}
                    placeholder="Transaction reference ID"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Notes</Label>
                  <Input
                    type="textarea"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Additional notes or comments"
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button 
                type="button" 
                color="secondary" 
                onClick={() => navigate("/invoices")}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                color="primary" 
                disabled={loading}
              >
                <FaSave className="me-1" />
                {loading ? "Creating..." : "Create Invoice"}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default CreateInvoice;